import {
  useCallback,
  useId,
  useMemo,
  useRef,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { clamp, fineStep, quantize } from "./math.js";

export type AdsrValue = {
  attack: number;
  decay: number;
  sustain: number;
  release: number;
};

export type AdsrEditorProps = {
  value: AdsrValue;
  onChange: (value: AdsrValue) => void;
  /** Max seconds for A/D/R time axes. */
  maxTime?: number;
  step?: number;
  label?: string;
  className?: string;
};

type Stage = keyof AdsrValue;

const STAGES: Stage[] = ["attack", "decay", "sustain", "release"];

const STAGE_LABEL: Record<Stage, string> = {
  attack: "Attack",
  decay: "Decay",
  sustain: "Sustain",
  release: "Release",
};

function stageBounds(stage: Stage, maxTime: number) {
  if (stage === "sustain") return { min: 0, max: 1 };
  return { min: 0, max: maxTime };
}

export function AdsrEditor({
  value,
  onChange,
  maxTime = 2,
  step = 0.01,
  label = "Envelope",
  className,
}: AdsrEditorProps) {
  const groupId = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const dragStage = useRef<Stage | null>(null);

  const width = 280;
  const height = 120;
  const pad = 10;

  const points = useMemo(() => {
    const a = clamp(value.attack, 0, maxTime);
    const d = clamp(value.decay, 0, maxTime);
    const s = clamp(value.sustain, 0, 1);
    const r = clamp(value.release, 0, maxTime);
    const total = Math.max(a + d + r, 0.0001);
    const usableW = width - pad * 2;
    const usableH = height - pad * 2;
    const x = (t: number) => pad + (t / total) * usableW;
    const y = (level: number) => pad + (1 - level) * usableH;
    return {
      p0: { x: pad, y: y(0) },
      p1: { x: x(a), y: y(1) },
      p2: { x: x(a + d), y: y(s) },
      p3: { x: x(a + d + r * 0.35), y: y(s) },
      p4: { x: x(a + d + r), y: y(0) },
      handles: {
        attack: { x: x(a), y: y(1), stage: "attack" as const },
        decay: { x: x(a + d), y: y(s), stage: "decay" as const },
        sustain: { x: x(a + d), y: y(s), stage: "sustain" as const },
        release: { x: x(a + d + r), y: y(0), stage: "release" as const },
      },
    };
  }, [maxTime, value.attack, value.decay, value.release, value.sustain]);

  const setStage = useCallback(
    (stage: Stage, next: number) => {
      const { min, max } = stageBounds(stage, maxTime);
      onChange({ ...value, [stage]: quantize(next, min, max, step) });
    },
    [maxTime, onChange, step, value],
  );

  const applyPointer = (stage: Stage, clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const nx = clamp((clientX - rect.left) / rect.width, 0, 1);
    const ny = clamp((clientY - rect.top) / rect.height, 0, 1);
    if (stage === "sustain") {
      setStage("sustain", 1 - ny);
      return;
    }
    if (stage === "attack") {
      setStage("attack", nx * maxTime);
      return;
    }
    if (stage === "decay") {
      setStage("decay", nx * maxTime);
      return;
    }
    setStage("release", nx * maxTime);
  };

  const onHandlePointerDown = (stage: Stage) => (event: ReactPointerEvent<SVGCircleElement>) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStage.current = stage;
    applyPointer(stage, event.clientX, event.clientY);
  };

  const onHandlePointerMove = (event: ReactPointerEvent<SVGCircleElement>) => {
    const stage = dragStage.current;
    if (!stage || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    applyPointer(stage, event.clientX, event.clientY);
  };

  const onHandlePointerUp = (event: ReactPointerEvent<SVGCircleElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragStage.current = null;
  };

  const onStageKeyDown = (stage: Stage) => (event: KeyboardEvent<SVGCircleElement>) => {
    const { min, max } = stageBounds(stage, maxTime);
    const unit = event.shiftKey ? fineStep(step, min, max) : step;
    const current = value[stage];
    switch (event.key) {
      case "ArrowRight":
      case "ArrowUp":
        event.preventDefault();
        setStage(stage, current + unit);
        break;
      case "ArrowLeft":
      case "ArrowDown":
        event.preventDefault();
        setStage(stage, current - unit);
        break;
      case "Home":
        event.preventDefault();
        setStage(stage, min);
        break;
      case "End":
        event.preventDefault();
        setStage(stage, max);
        break;
      default:
        break;
    }
  };

  const path = `M ${points.p0.x} ${points.p0.y} L ${points.p1.x} ${points.p1.y} L ${points.p2.x} ${points.p2.y} L ${points.p3.x} ${points.p3.y} L ${points.p4.x} ${points.p4.y}`;

  return (
    <div
      role="group"
      aria-label={label}
      className={["flex flex-col gap-2", className].filter(Boolean).join(" ")}
    >
      <div className="text-[11px] uppercase tracking-[0.08em] text-ink-muted">{label}</div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full max-w-[280px] rounded-[var(--radius-panel)] border border-hairline bg-panel"
        preserveAspectRatio="xMidYMid meet"
      >
        <path d={path} fill="none" stroke="var(--color-signal)" strokeWidth="2" />
        {STAGES.map((stage) => {
          const handle = points.handles[stage];
          const { min, max } = stageBounds(stage, maxTime);
          const now = value[stage];
          return (
            <circle
              key={stage}
              cx={handle.x}
              cy={handle.y}
              r={6}
              fill="var(--color-panel-hi)"
              stroke="var(--color-signal)"
              strokeWidth="1.5"
              role="slider"
              tabIndex={0}
              aria-label={STAGE_LABEL[stage]}
              aria-valuemin={min}
              aria-valuemax={max}
              aria-valuenow={now}
              aria-valuetext={`${STAGE_LABEL[stage]} ${now}`}
              className="cursor-pointer outline-none"
              onPointerDown={onHandlePointerDown(stage)}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              onPointerCancel={onHandlePointerUp}
              onKeyDown={onStageKeyDown(stage)}
            />
          );
        })}
      </svg>
      <div
        id={`${groupId}-readout`}
        className="readout grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] text-ink-muted sm:grid-cols-4 sm:gap-2"
      >
        {STAGES.map((stage) => (
          <span key={stage}>
            {stage[0]?.toUpperCase()}:{value[stage].toFixed(2)}
          </span>
        ))}
      </div>
    </div>
  );
}
